import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "../../lib/api";
import { fmtDate, TEAM_LABELS } from "../../lib/format";
import SectionHeading from "../SectionHeading";
import { Reveal } from "../Reveal";
import EmptyState from "../EmptyState";

export default function HomeResults() {
  const [results, setResults] = useState(null);

  useEffect(() => {
    api.get("/results").then((r) => setResults(r.data)).catch(() => setResults([]));
  }, []);

  const list = results === null ? [] : results.slice(0, 3);

  const bestPlace = (r) => {
    const places = (r.coureurs || []).map((c) => parseInt(String(c.place).replace(/[^\d]/g, ""), 10)).filter((n) => !Number.isNaN(n));
    return places.length ? String(Math.min(...places)) : "—";
  };
  const ridersLine = (r) => (r.coureurs || []).slice(0, 3).map((c) => `${c.prenom ? `${c.prenom} ` : ""}${c.nom} — ${c.place}`).join(" · ");

  return (
    <section className="bg-navy2 py-24 sm:py-32">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <SectionHeading number="03" kicker="Performance" title={<>Derniers résultats</>} dark />
          <Reveal delay={0.2}>
            <Link
              to="/resultats"
              data-testid="results-see-all"
              className="group inline-flex items-center gap-3 border border-white/30 text-white font-heading uppercase tracking-wider text-sm px-6 py-3.5 hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300"
            >
              Voir tous les résultats
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
          </Reveal>
        </div>

        {results !== null && list.length === 0 ? (
          <div className="border border-dashed border-white/20 px-8 py-14 text-center" data-testid="home-results-empty">
            <p className="font-heading uppercase font-semibold tracking-tight text-white text-2xl">Aucun résultat publié</p>
            <p className="mt-2 text-sm text-white/60">Les résultats des coureurs et coureuses du club seront publiés ici au fil des compétitions.</p>
          </div>
        ) : (
          <div data-testid="home-results-list" className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {list.map((r, i) => (
              <Reveal key={r.id} delay={i * 0.08}>
                <Link
                  to="/resultats"
                  data-testid={`home-result-${r.id}`}
                  className="group block bg-navy border border-white/10 p-7 hover:border-rouge/60 transition-[border-color] duration-300 h-full"
                >
                  <div className="flex items-start justify-between gap-4">
                    <span className="font-heading font-semibold text-5xl text-outline-white leading-none group-hover:text-rouge group-hover:[-webkit-text-stroke:0px] transition-all duration-300">
                      {bestPlace(r)}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/50 border border-white/15 px-2.5 py-1">
                      {r.categorie || TEAM_LABELS[r.team] || r.team}
                    </span>
                  </div>
                  <h3 className="mt-5 font-heading uppercase font-semibold tracking-tight text-white text-xl leading-tight">
                    {r.name}
                  </h3>
                  <p className="mt-2 text-sm text-white/60">{ridersLine(r)}</p>
                  <p className="mt-4 text-xs uppercase tracking-widest text-white/40">
                    {fmtDate(r.date)}{r.location ? ` · ${r.location}` : ""}
                    {r.resultat_equipe && <> — <span className="text-rouge">Équipe {r.resultat_equipe}</span></>}
                  </p>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
